import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MentionInput } from '@/components/shared/MentionInput';
import { Trash2, MessageCircle } from 'lucide-react';
import { STATUS_META, STATUS_ORDER, subtaskStats } from '@/constants/projectMeta';
import { SubtaskList } from '@/components/projects/SubtaskList';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const renderMentions = (text) => text.split(/(@\w+)/g).map((part, i) => (
  part.startsWith('@')
    ? <span key={i} className="text-[hsl(var(--primary))] font-medium">{part}</span>
    : <span key={i}>{part}</span>
));

const labelCls = 'text-xs text-[hsl(var(--text-muted))]';

const UpdateForm = ({ form, set, shownProgress, autoProgress }) => (
  <div className="space-y-3">
    <div>
      <label className={labelCls}>Current update</label>
      <Textarea rows={2} value={form.update} onChange={(e) => set('update', e.target.value)}
        placeholder="What moved forward this week? (optional)" data-testid="update-text-input" />
    </div>
    <div>
      <label className={labelCls}>Issue / Blocker</label>
      <Textarea rows={2} value={form.issue} onChange={(e) => set('issue', e.target.value)}
        placeholder="What is blocking progress? (leave empty if none)" data-testid="update-issue-input" />
    </div>
    <div>
      <label className={labelCls}>Next step</label>
      <Input value={form.next_step} onChange={(e) => set('next_step', e.target.value)}
        placeholder="What happens next" data-testid="update-next-input" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className={labelCls}>Progress %</label>
        <Input type="number" min={0} max={100} value={shownProgress} disabled={autoProgress}
          onChange={(e) => set('progress', Number(e.target.value))} data-testid="update-progress-input" />
        {autoProgress && (
          <p className="text-[10px] mt-1 text-[hsl(var(--text-muted))]" data-testid="update-progress-auto-hint">Auto from subtasks average</p>
        )}
      </div>
      <div>
        <label className={labelCls}>Status</label>
        <Select value={form.status} onValueChange={(v) => set('status', v)}>
          <SelectTrigger data-testid="update-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

const UpdateTimeline = ({ history, canManage, meId, onDelete }) => (
  <div className="mt-4">
    <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2">Update Timeline</p>
    {!history.length && <p className="text-xs text-[hsl(var(--text-muted))]">No updates yet.</p>}
    <div className="space-y-2" data-testid="update-timeline">
      {history.map((u) => (
        <div key={u.id} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2.5" data-testid={`update-entry-${u.id}`}>
          <div className="flex items-center justify-between text-[11px] text-[hsl(var(--text-muted))]">
            <span data-testid={`update-meta-${u.id}`}>
              {u.week} · {fmtDateTime(u.created_at)} · {u.by_name} · {u.progress}%
            </span>
            {(canManage || u.by_id === meId) && (
              <button onClick={() => onDelete(u)} className="hover:text-red-400" data-testid={`update-delete-${u.id}`}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
          {u.update && <p className="text-xs mt-1 text-[hsl(var(--text-primary))]">{u.update}</p>}
          {u.issue && <p className="text-xs mt-1 text-red-400">Issue: {u.issue}</p>}
          {u.next_step && <p className="text-[11px] mt-1 text-[hsl(var(--text-muted))]">Next: {u.next_step}</p>}
        </div>
      ))}
    </div>
  </div>
);

const CommentSection = ({ comments, canManage, meId, onPost, onDelete }) => {
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const post = async () => {
    const text = comment.trim();
    if (!text) return;
    setPosting(true);
    try {
      await onPost(text);
      setComment('');
    } finally {
      setPosting(false);
    }
  };
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2 flex items-center gap-1.5">
        <MessageCircle size={12} /> Comments ({comments.length})
      </p>
      <div className="mb-2.5" data-testid="comment-input-wrap">
        <MentionInput value={comment} onChange={setComment} onSubmit={post} submitting={posting}
          placeholder="Write a comment... (type @ to mention)" />
      </div>
      <div className="space-y-2" data-testid="comment-list">
        {!comments.length && <p className="text-xs text-[hsl(var(--text-muted))]">No comments yet.</p>}
        {comments.map((c) => (
          <div key={c.id} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2.5" data-testid={`comment-entry-${c.id}`}>
            <div className="flex items-center justify-between text-[11px] text-[hsl(var(--text-muted))]">
              <span>{c.by_name} · {fmtDateTime(c.created_at)}</span>
              {(canManage || c.by_id === meId) && (
                <button onClick={() => onDelete(c)} className="hover:text-red-400" data-testid={`comment-delete-${c.id}`}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <p className="text-xs mt-1 text-[hsl(var(--text-primary))] whitespace-pre-wrap">{renderMentions(c.text)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TaskUpdateDialog = ({ open, onOpenChange, task, week, onSubmit, onDeleteUpdate, onAddComment, onDeleteComment, onAddSubtask, onPatchSubtask, onDeleteSubtask, onAssignSubtask, members = [], canManage, meId }) => {
  const [form, setForm] = useState(() => ({
    update: '', issue: '', next_step: '', progress: task?.progress || 0, status: task?.status || 'in_progress',
  }));
  const [saving, setSaving] = useState(false);

  if (!task) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const subtasks = task.subtasks || [];
  const avg = subtaskStats(subtasks).avg;
  const autoProgress = avg !== null && form.status !== 'completed';
  const shownProgress = form.status === 'completed' ? 100 : (autoProgress ? avg : form.progress);

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit(task, { ...form, progress: shownProgress, week });
      onOpenChange(false);
    } catch (e) {
      // toast already shown by the page handler
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-update-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{task.title}</DialogTitle>
          <DialogDescription>
            Weekly update — {week} · PIC {task.pic_name || '—'}{task.project ? ` · ${task.project}` : ''}
            {task.date_added ? ` · Added ${task.date_added}` : ''}
          </DialogDescription>
        </DialogHeader>

        <UpdateForm form={form} set={set} shownProgress={shownProgress} autoProgress={autoProgress} />

        <SubtaskList
          subtasks={subtasks}
          members={members}
          onAdd={(title) => onAddSubtask(task, title)}
          onPatch={(s, patch) => onPatchSubtask(task, s, patch)}
          onAssign={(s, memberId) => onAssignSubtask(task, s, memberId)}
          onDelete={(s) => onDeleteSubtask(task, s)}
        />

        <UpdateTimeline history={[...(task.updates || [])].reverse()} canManage={canManage} meId={meId}
          onDelete={(u) => onDeleteUpdate(task, u)} />

        <CommentSection comments={[...(task.comments || [])].reverse()} canManage={canManage} meId={meId}
          onPost={(text) => onAddComment(task, text)} onDelete={(c) => onDeleteComment(task, c)} />

        <DialogFooter className="sticky -bottom-6 -mx-6 -mb-6 px-6 py-3 border-t border-[hsl(var(--border))] bg-background z-10">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="update-close-btn">Close</Button>
          <Button onClick={submit} disabled={saving} data-testid="update-save-btn">
            {saving ? 'Saving…' : 'Submit Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
